import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, ts } from "@/lib/api";
import { PageHeader, Panel, Th, Td } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { OrderIdocPanel } from "@/components/sap/IdocPanel";
import { LinkRequestsPanel } from "@/components/LinkRequestsPanel";

const Totals = ({ rows }) => (
  <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="summary-totals">
    {rows.map((t) => (
      <div key={t.customer} className="rounded-sm border border-slate-800 bg-[#1F232C] p-4" data-testid={`summary-total-${t.customer}`}>
        <div className="text-xs text-slate-400">{t.customer}</div>
        <div className="mt-1 font-mono text-2xl text-slate-100">{t.orders}</div>
        <div className="text-[11px] text-slate-500">orders · {t.idocs} IDocs{t.errors ? ` · ${t.errors} IDoc errors` : ""}</div>
      </div>))}
  </div>
);

const Row = ({ o, open, toggle, load }) => {
  const del = () => {
    if (!window.confirm(`Delete order ${o.po_number}? It is moved to the archive so the PO can be uploaded again. Delete its IDoc first if SAP has not collected it.`)) return;
    api.delete(`/orders/${o.id}`).then(() => { toast.success(`Order ${o.po_number} deleted`); load(); }).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <Fragment>
      <tr data-testid={`summary-row-${o.po_number}`}>
        <Td><button type="button" onClick={toggle} className="text-slate-400 hover:text-white" data-testid={`summary-expand-${o.po_number}`}>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button></Td>
        <Td className="whitespace-nowrap text-xs">{ts(o.created_at)}</Td><Td className="font-mono">{o.po_number}</Td><Td>{o.customer}</Td>
        <Td className="text-xs">{o.source}</Td><Td>{o.lines}</Td><Td>{o.qty.toLocaleString()}</Td>
        <Td className="font-mono text-xs">{o.idoc_file || (o.idoc_status === "error" ? <span className="text-rose-300">{o.idoc_error || "error"}</span> : "-")}</Td>
        <Td><StatusBadge status={o.status} /></Td><Td className="text-xs">{o.created_by}</Td>
        <Td><button type="button" onClick={del} className="text-slate-500 hover:text-rose-400" data-testid={`summary-delete-${o.po_number}`}><Trash2 className="h-4 w-4" /></button></Td>
      </tr>
      {open && <tr><td colSpan={11} className="px-4 pb-4"><OrderIdocPanel o={{ ...o, order_type: "customer" }} onChanged={load} /></td></tr>}
    </Fragment>
  );
};

export default function MySummary() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/my/summary").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <div>
      <PageHeader title="My summary" subtitle="Every PO uploaded for your customers, with its SAP IDoc. Open a row to download, delete or re-create the IDoc." />
      <Totals rows={d.totals} />
      <LinkRequestsPanel />
      <Panel title={`Uploaded orders (${d.orders.length})`} testId="summary-orders-panel">
        {!d.orders.length ? <p className="px-5 py-4 text-xs text-slate-500" data-testid="summary-empty">Nothing uploaded yet.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="border-b border-slate-800"><tr><Th></Th><Th>Created</Th><Th>PO</Th><Th>Customer</Th><Th>Source</Th><Th>Lines</Th><Th>Qty</Th><Th>SAP IDoc</Th><Th>Status</Th><Th>By</Th><Th></Th></tr></thead>
            <tbody className="divide-y divide-slate-800/70">{d.orders.map((o) => <Row key={o.id} o={o} open={open === o.id} toggle={() => setOpen(open === o.id ? null : o.id)} load={load} />)}</tbody>
          </table></div>)}
      </Panel>
    </div>
  );
}
